import { css, type SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import type { CodeEnum } from '@zelos/codegen/apis';
import { V1Service } from '@zelos/codegen/apis';
import { classificationMeta, tryoutType } from '@zelos/shared/constants/meta';
import { getFormattedDate, isAfterNow } from '@zelos/shared/lib/date-util';
import { useCommQueryData } from '@zelos/shared/lib/use-comm-query-data';
import Link from 'next/link';
import Skeleton from 'react-loading-skeleton';
import Button from '@/components/comm/Button';
import Chip from '@/components/comm/Chip';
import ClassificationChip from '@/components/comm/ClassificationChip';
import DdayChip from '@/components/comm/DdayChip';
import Divider from '@/components/comm/Divider';
import { breakpointMedium } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';
import { testIds } from '@/constants/test-ids';
import { useCustomHackleTrack } from '@/lib/hackle.service';

type Layout = 'default' | 'narrow';

export type MainTryoutsInfoProps = {
  classification?: string,
  overrideCss?: {
    container?: SerializedStyles,
    item?: SerializedStyles,
  },
  layout?: Layout,
};

const DISPLAY_SKELETON_COUNT = 1;

const MainTryoutsInfo = ({
  classification,
  overrideCss,
  layout = 'default',
}: MainTryoutsInfoProps) => {
  const {
    track,
  } = useCustomHackleTrack();

  const { classificationList } = useCommQueryData();

  const { data: tryoutsList, isFetching } = useQuery(
    ['v1TryoutsLandingList', classificationList, classification],
    () => V1Service.v1TryoutsLandingList({
      classification: classificationList?.find(({ code }) => code === classification)?.id,
    }),
    {
      enabled: !!classificationList,
      refetchOnWindowFocus: false,
      select: (data) => {
        const displayOrder: CodeEnum[] = ['PCCP', 'PCCE', 'PCSQL'];
        return displayOrder.flatMap((code) =>
          data.filter(({ classification: { code: targetCode } }) => targetCode === code));
      },
    },
  );

  return (
    <Container data-testid={testIds.main.Tryouts.Banners} css={overrideCss?.container}>
      {(tryoutsList?.length && !isFetching) ? tryoutsList.map(({
        id,
        classification: { code },
        type,
        startAt,
        registrationStartDatetime,
        registrationEndDatetime,
        isApplied,
      }) => (
        <Item key={id} css={overrideCss?.item}>
          <Content>
            <Hgroup>

              <Chips>
                <ClassificationChip code={code} />
                <Chip
                  txt={tryoutType[type]}
                  size="wide"
                  border={`1px solid ${colors.lightGrey2}`}
                  backgroundColor={colors.white}
                  textColor={colors.indigo}
                  bold
                />
              </Chips>
              <Title>
                {classificationMeta[code].title}시험
              </Title>
            </Hgroup>
            <Times layout={layout}>
              <div>
                <TimeLabel>시험 일시</TimeLabel>
                <TimeContent>{startAt && getFormattedDate(startAt, 'YYYY-MM-DD(dd) HH:mm')}</TimeContent>
              </div>
              <div>
                <TimeLabel>접수 마감</TimeLabel>
                <TimeContent>{getFormattedDate(registrationEndDatetime, 'YYYY-MM-DD(dd) HH:mm')}</TimeContent>
                <DdayChipWrapper>
                  <DdayChip date={registrationEndDatetime} />
                </DdayChipWrapper>
              </div>
            </Times>
          </Content>
          {isAfterNow(registrationStartDatetime) && (
            <ApplyTryoutButton disabled>접수예정</ApplyTryoutButton>
          ) ||
              isApplied && (
                <ApplyTryoutButton disabled>접수완료</ApplyTryoutButton>
              ) ||
              (
                <Link
                  href={{
                    pathname: '/tryouts/apply/select',
                    query: {
                      tryout_id: id,
                      referrer: globalThis.window?.location.pathname || '',
                    },
                  }}
                  onClick={() => {
                    code === 'PCCP' && track({ key: 'certi_pccp_apply_clicked' });
                    code === 'PCCE' && track({ key: 'certi_pcce_apply_clicked' });
                    code === 'PCSQL' && track({
                      key: 'certi_pcsql_apply_clicked',
                      properties: {
                        entry_route: window.location.pathname,
                      },
                    });
                  }}
                >
                  <ApplyTryoutButton>접수하기</ApplyTryoutButton>
                </Link>
              )}
        </Item>
      ))
        : (
          <>
            {
              [...Array(DISPLAY_SKELETON_COUNT).keys()].map((value) => (
                <ItemSkeleton key={value} />
              ))
            }
          </>
        )}
    </Container>
  );
};

export default MainTryoutsInfo;

const ItemSkeleton = () => {
  return (
    <Item>
      <Chips>
        <Skeleton height="100%" width={100} />
      </Chips>
      <Title>
        <Skeleton width={150} />
      </Title>
      <Divider
        color={colors.lightGrey2}
        margin={[
          '16px',
          0,
          0,
        ].join(' ')}
      />
      <Times>
        <div>
          <Skeleton height="100%" width={200} />
        </div>
        <div>
          <Skeleton height="100%" width={200} />
        </div>
      </Times>
      <Skeleton height={46} />
    </Item>
  );
};

const Container = styled.div`
  display: grid;
  width: 100%;
  justify-content: start;
  padding-top: 40px;
  column-gap: 30px;
  grid-template-columns: repeat(3, 1fr);

  @media (max-width: ${breakpointMedium}) {
    display: grid;
    justify-content: stretch;
    padding-top: 30px;
    grid-template-columns: repeat(1, 1fr);
    row-gap: 30px;
  }
`;

const Item = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  padding: 32px;
  border: 1px solid ${colors.lightGrey2};
  background-color: ${colors.white};
  border-radius: 15px;
  gap: 32px;

  @media (max-width: ${breakpointMedium}) {
    min-width: auto;
    flex: 1;
    padding: 32px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Hgroup = styled.hgroup`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  padding-bottom: 16px;
  border-bottom: 1px solid ${colors.lightGrey2};
  gap: 24px;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 5px;
  row-gap: 5px;
`;

const Title = styled.div`
  color: ${colors.black};
  font-size: 25px;
  font-weight: 700;
  line-height: 36.2px;

  @media (max-width: ${breakpointMedium}) {
    font-size: 18px;
    line-height: 26.06px;
  }
`;

const ApplyTryoutButton = styled(Button)`
  width: 100%;
  height: 46px;
  background-color: ${colors.blue};
  border-radius: 4px;
  transition: all 0.2s ease-out;
`;

const timesGridArea = {
  label: 'label',
  content: 'content',
  chip: 'chip',
};

const timesCommonStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const timesDefaultStyle = css`
  & > div {
    display: flex;
    align-items: center;
  }
`;

const timesNarrowStyle = css`
  & {
    > div {
      display: grid;
      row-gap: 4px;
    }

    > div:nth-of-type(1) {
      grid-template-areas: 
      'label'
      'content'
    }

    > div:nth-of-type(2) {
      grid-template-areas: 
      'label .'
      'content chip'
    }
  }
`;

const Times = styled.div<{ layout?: Layout }>`
  ${timesCommonStyle}
  ${({ layout }) => layout === 'narrow' && timesNarrowStyle || timesDefaultStyle}
`;

const TimeLabel = styled.span`
  flex-shrink: 0;
  margin-right: 24px;
  color: ${colors.black};
  font-size: 13px;
  font-weight: 700;
  grid-area: ${timesGridArea.label};
  line-height: normal;

  @media (max-width: ${breakpointMedium}) {
    font-size: 14px;
  }
`;

const TimeContent = styled.span`
  flex-shrink: 0;
  margin-right: 6px;
  color: ${colors.black};
  font-size: 13px;
  font-weight: 400;
  grid-area: ${timesGridArea.content};
  line-height: 22px;

  @media (max-width: ${breakpointMedium}) {
    font-size: 14px;
  }
`;

const DdayChipWrapper = styled.div`
  align-self: end;
  grid-area: ${timesGridArea.chip};
`;
